import { useCallback, useEffect, useState } from "react";
import { Plus, Copy, Trash, LinkSimple, ArrowSquareOut, ClockCounterClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import LinkHistoryDrawer from "../components/LinkHistoryDrawer";
import { listLinks, createLink, deleteLink, redirectUrl } from "../lib/api";

export default function AffiliateManager() {
  const [links, setLinks] = useState([]);
  const [name, setName] = useState("");
  const [url, setUrl] = useState("");
  const [tags, setTags] = useState("");
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [historyLink, setHistoryLink] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const l = await listLinks();
      setLinks(l);
    } catch (err) {
      console.error("Failed to load links:", err);
      toast.error("Failed to load links");
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const create = async () => {
    if (!name.trim() || !url.trim()) return toast.error("Name & URL required");
    setLoading(true);
    try {
      await createLink({
        name: name.trim(),
        url: url.trim(),
        tags: tags.split(",").map((t) => t.trim()).filter(Boolean),
      });
      toast.success("Link saved");
      setName("");
      setUrl("");
      setTags("");
      setOpen(false);
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setLoading(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this link?")) return;
    await deleteLink(id);
    refresh();
    toast.success("Deleted");
  };

  const copyTracker = (id) => {
    const link = redirectUrl(id);
    navigator.clipboard.writeText(link);
    toast.success("Tracking URL copied");
  };

  return (
    <div className="max-w-6xl">
      <div className="flex items-start justify-between mb-10 flex-wrap gap-4">
        <div>
          <div className="label-mono mb-3">library</div>
          <h1 className="font-display text-4xl sm:text-5xl font-light tracking-tighter">
            My <span className="font-black text-[#ccff00]">Affiliate</span> Links
          </h1>
          <p className="text-zinc-400 mt-3 max-w-lg leading-relaxed">
            Save affiliate URLs, generate trackable short links, and monitor
            click volume in real time.
          </p>
        </div>

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button
              data-testid="add-link-btn"
              className="bg-[#ccff00] text-black hover:bg-[#b3e600] font-semibold px-6 py-3 flex items-center gap-2 transition-colors"
            >
              <Plus size={18} weight="bold" />
              Add link
            </button>
          </DialogTrigger>
          <DialogContent className="bg-[#0a0a0a] border border-white/10 rounded-none max-w-md">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl">Add affiliate link</DialogTitle>
              <DialogDescription className="text-zinc-500 text-sm">
                Save an affiliate URL and get a trackable redirect link.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-3">
              <div>
                <div className="label-mono mb-2">name</div>
                <Input
                  data-testid="link-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Amazon Prime"
                  className="rounded-none bg-[#121212] border-white/10 h-11"
                />
              </div>
              <div>
                <div className="label-mono mb-2">affiliate URL</div>
                <Input
                  data-testid="link-url"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="https://…?tag=abc"
                  className="rounded-none bg-[#121212] border-white/10 h-11 font-mono text-xs"
                />
              </div>
              <div>
                <div className="label-mono mb-2">tags (comma-separated)</div>
                <Input
                  data-testid="link-tags"
                  value={tags}
                  onChange={(e) => setTags(e.target.value)}
                  placeholder="fitness, supplements"
                  className="rounded-none bg-[#121212] border-white/10 h-11"
                />
              </div>
            </div>
            <DialogFooter>
              <button
                data-testid="save-link-btn"
                onClick={create}
                disabled={loading}
                className="bg-[#ccff00] text-black hover:bg-[#b3e600] disabled:opacity-40 px-6 py-2.5 font-semibold w-full"
              >
                {loading ? "Saving…" : "Save link"}
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-[#0a0a0a] border border-white/10" data-testid="links-table">
        <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-3 border-b border-white/10 label-mono">
          <div className="col-span-3">name</div>
          <div className="col-span-4">url</div>
          <div className="col-span-1 text-right">clicks</div>
          <div className="col-span-1 text-right">ads</div>
          <div className="col-span-3 text-right">actions</div>
        </div>

        {links.length === 0 && (
          <div className="px-6 py-16 text-center text-zinc-500">
            <LinkSimple size={40} className="mx-auto mb-3 opacity-40" />
            <div className="font-mono text-sm">no links yet — add one to get started</div>
          </div>
        )}

        {links.map((l) => (
          <div
            key={l.id}
            data-testid={`link-row-${l.id}`}
            className="grid grid-cols-1 md:grid-cols-12 gap-4 px-6 py-4 border-b border-white/5 hover:bg-white/[0.02] items-center"
          >
            <div className="col-span-3">
              <div className="font-display font-bold text-white">{l.name}</div>
              {l.tags?.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-1">
                  {l.tags.map((t, i) => (
                    <span key={`${t}-${i}`} className="font-mono text-[10px] text-zinc-500">
                      #{t}
                    </span>
                  ))}
                </div>
              )}
            </div>
            <div className="col-span-4 font-mono text-xs text-zinc-400 truncate">
              <a href={l.url} target="_blank" rel="noreferrer" className="hover:text-[#ccff00]">
                {l.url}
              </a>
            </div>
            <div className="col-span-1 text-right font-mono font-bold text-[#ccff00]">
              {l.clicks}
            </div>
            <div className="col-span-1 text-right font-mono text-zinc-300">
              {l.ads_generated}
            </div>
            <div className="col-span-3 flex justify-end gap-2">
              <button
                onClick={() => setHistoryLink(l)}
                data-testid={`history-${l.id}`}
                className="p-2 bg-[#121212] border border-white/10 hover:border-[#ccff00] hover:text-[#ccff00] transition-colors"
                title="Ad history for this link"
              >
                <ClockCounterClockwise size={16} />
              </button>
              <button
                onClick={() => copyTracker(l.id)}
                data-testid={`copy-tracker-${l.id}`}
                className="p-2 bg-[#121212] border border-white/10 hover:border-[#ccff00] hover:text-[#ccff00] transition-colors"
                title="Copy tracked URL"
              >
                <Copy size={16} />
              </button>
              <a
                href={redirectUrl(l.id)}
                target="_blank"
                rel="noreferrer"
                className="p-2 bg-[#121212] border border-white/10 hover:border-[#ccff00] hover:text-[#ccff00] transition-colors"
                title="Test click"
              >
                <ArrowSquareOut size={16} />
              </a>
              <button
                onClick={() => remove(l.id)}
                data-testid={`delete-link-${l.id}`}
                className="p-2 bg-[#121212] border border-white/10 hover:border-red-500 hover:text-red-500 transition-colors"
                title="Delete"
              >
                <Trash size={16} />
              </button>
            </div>
          </div>
        ))}
      </div>

      <LinkHistoryDrawer
        open={!!historyLink}
        onOpenChange={(o) => !o && setHistoryLink(null)}
        link={historyLink}
      />
    </div>
  );
}
